// src/components/EditModal.jsx
import { useState, useRef } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { ref, deleteObject, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function EditModal({ post, onClose, onSuccess }) {
  const [diagDate, setDiagDate] = useState(post.diagDate || "");
  const [client, setClient] = useState(post.client || "");
  const [capacity, setCapacity] = useState(post.capacity || "");
  const [files, setFiles] = useState(
    [...(post.files || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  );

// 🔥 핵심: 삭제할 파일들을 잠시 담아두는 바구니
  const [filesToDelete, setFilesToDelete] = useState([]);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(files);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setFiles(reordered);
  };


  // ── 파일 제거 (UI에서만 제거하고 삭제 예약) ────────────────
  const handleRemoveFile = (idx) => {
    const fileToRemove = files[idx];

    // 이미 서버에 업로드되어 있던 파일인 경우에만 삭제 예약 리스트에 추가
    if (fileToRemove.url) {
      setFilesToDelete((prev) => [...prev, fileToRemove]);
    }

    // UI 상태에서는 즉시 제거
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── 파일 추가 (기존 로직 유지) ──────────────────────────
  const handleAddFiles = async (e) => {
    const selected = Array.from(e.target.files).filter(
      (f) => f.type === "application/pdf"
    );
    if (selected.length === 0) return;

    const existingNames = new Set(files.map((f) => f.name));
    const newFiles = selected.filter((f) => !existingNames.has(f.name));
    if (newFiles.length === 0) {
      setError("이미 동일한 파일명이 존재합니다.");
      return;
    }

    setUploading(true);
    setError("");
    const uploaded = [];

    try {
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const storageRef = ref(storage, `posts/${post.slug}/${file.name}`);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, file);
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                ((i + snap.bytesTransferred / snap.totalBytes) / newFiles.length) * 100
              );
              setUploadProgress(pct);
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploaded.push({ name: file.name, url, size: file.size, order: 0 });
              resolve();
            }
          );
        });
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } catch {
      setError("파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = "";
    }
  };

  // ── 최종 저장 (이때 실제 삭제가 일어남) ──────────────────
  const handleSave = async () => {
    if (!diagDate || !client || !capacity) {
      setError("모든 항목을 입력해주세요.");
      return;
    }
    setSaving(true);

    try {
      // 1. 삭제 예약된 파일들을 Storage에서 실제로 삭제
      for (const file of filesToDelete) {
        try {
          const fileRef = ref(storage, `posts/${post.slug}/${file.name}`);
          await deleteObject(fileRef);
        } catch (e) {
          // 파일이 이미 없거나 삭제 실패해도 프로세스 진행을 위해 경고만 출력
          console.warn("Storage 삭제 실패 (무시):", e.message);
        }
      }

      // 2. DB 업데이트
      const newTitle = `${diagDate}_${client}_${capacity}`;
      const updatedFiles = files.map((f, i) => ({ ...f, order: i }));
      
      await updateDoc(doc(db, "posts", post.id), {
        diagDate,
        client,
        capacity,
        title: newTitle,
        files: updatedFiles,
      });

      onSuccess();
    } catch (e) {
      console.error(e);
      setError("저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || uploading;

  
  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerTitle}>게시물 수정</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.field}>
            <label style={styles.label}>진단 날짜</label>
            <input
              style={styles.input}
              type="date"
              value={diagDate}
              onChange={(e) => setDiagDate(e.target.value)}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>고객사</label>
            <input
              style={styles.input}
              type="text"
              value={client}
              onChange={(e) => setClient(e.target.value)}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>수전용량</label>
            <input
              style={styles.input}
              type="text"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </div>

          <div style={styles.previewLabel}>변경 후 제목 미리보기</div>
          <div style={styles.preview}>
            {diagDate || "YYYY-MM-DD"}_{client || "고객사"}_{capacity || "용량"}
          </div>

          {/* 파일 목록 헤더 */}
          <div style={styles.fileListSection}>
            <div style={styles.fileListHeader}>
              <span style={styles.label}>첨부 파일 ({files.length}개)</span>
              <div style={styles.fileListActions}>
                {files.length > 1 && (
                  <span style={styles.fileListHint}>↕ 드래그로 순서 변경</span>
                )}
                {/* ── 파일 추가 버튼 ── */}
                <button
                  style={{
                    ...styles.addFileBtn,
                    opacity: isBusy ? 0.5 : 1,
                    cursor: isBusy ? "not-allowed" : "pointer",
                  }}
                  onClick={() => !isBusy && fileInputRef.current.click()}
                  disabled={isBusy}
                  title="PDF 파일 추가"
                >
                  + 파일 추가
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handleAddFiles}
                  style={{ display: "none" }}
                />
              </div>
            </div>

            {/* 업로드 진행 바 */}
            {uploading && (
              <div style={styles.progressWrap}>
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
                </div>
                <span style={styles.progressText}>{uploadProgress}% 업로드 중...</span>
              </div>
            )}

            {files.length === 0 ? (
              <div style={styles.emptyFiles}>
                첨부된 파일이 없습니다. 위 버튼으로 추가하세요.
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="edit-files">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {files.map((file, idx) => (
                        <Draggable
                          key={file.name}
                          draggableId={file.name}
                          index={idx}
                        >
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              style={{
                                ...styles.fileItem,
                                background: snapshot.isDragging ? "#fef9c3" : "#ffffff",
                                boxShadow: snapshot.isDragging
                                  ? "0 4px 20px rgba(0,0,0,0.1)"
                                  : "none",
                                ...prov.draggableProps.style,
                              }}
                            >
                              <span
                                {...prov.dragHandleProps}
                                style={styles.dragHandle}
                                title="드래그하여 순서 변경"
                              >
                                ⠿
                              </span>
                              <span style={styles.fileNum}>{idx + 1}</span>
                              <span style={styles.fileIcon}>📄</span>
                              <span style={styles.fileName} title={file.name}>
                                {file.name}
                              </span>
                              <button
                                style={styles.removeBtn}
                                onClick={() => handleRemoveFile(idx)}
                                title="파일 삭제"
                                disabled={isBusy}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {/* 취소 버튼: 붉은색 */}
          <button
            style={{ ...styles.btn, ...styles.cancelBtn }}
            onClick={onClose}
            disabled={isBusy}
          >
            취소
          </button>
          {/* 저장 버튼: 파란색 */}
          <button
            style={{
              ...styles.btn,
              ...styles.saveBtn,
              opacity: isBusy ? 0.7 : 1,
              cursor: isBusy ? "not-allowed" : "pointer",
            }}
            onClick={handleSave}
            disabled={isBusy}
          >
            {saving ? "저장 중..." : uploading ? "업로드 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "520px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #fde68a",
    flexShrink: 0,
  },
  headerTitle: { color: "#111827", fontSize: "16px", fontWeight: "700" },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#6b7280",
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px",
  },
  body: { padding: "24px", overflowY: "auto", flex: 1 },
  field: { marginBottom: "16px" },
  label: {
    display: "block",
    color: "#6b7280",
    fontSize: "11px",
    fontWeight: "600",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    padding: "10px 12px",
    color: "#111827",
    fontSize: "13px",
    outline: "none",
    fontFamily: "inherit",
  },
  previewLabel: { color: "#9ca3af", fontSize: "11px", marginBottom: "6px", marginTop: "4px" },
  preview: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "6px",
    padding: "10px 14px",
    color: "#0369a1",
    fontSize: "13px",
    fontFamily: "monospace",
    wordBreak: "break-all",
    marginBottom: "20px",
  },
  fileListSection: { marginBottom: "8px" },
  fileListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  fileListActions: { display: "flex", alignItems: "center", gap: "10px" },
  fileListHint: { color: "#9ca3af", fontSize: "11px" },
  /* 파일 추가 버튼: 초록색 */
  addFileBtn: {
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
    fontFamily: "inherit",
    transition: "opacity 0.2s",
  },
  progressWrap: { marginBottom: "10px" },
  progressBar: { height: "4px", background: "#e5e7eb", borderRadius: "2px", overflow: "hidden", marginBottom: "5px" },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #1e6fd9, #60a5fa)", transition: "width 0.3s ease", borderRadius: "2px" },
  progressText: { color: "#6b7280", fontSize: "11px" },
  emptyFiles: {
    color: "#9ca3af",
    fontSize: "13px",
    padding: "16px",
    textAlign: "center",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    marginBottom: "6px",
    transition: "background 0.15s",
  },
  dragHandle: { color: "#9ca3af", cursor: "grab", fontSize: "16px", userSelect: "none", flexShrink: 0, lineHeight: 1 },
  fileNum: { color: "#9ca3af", fontSize: "11px", minWidth: "16px", textAlign: "center", flexShrink: 0 },
  fileIcon: { fontSize: "14px", flexShrink: 0 },
  fileName: { color: "#111827", fontSize: "13px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  removeBtn: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    borderRadius: "4px",
    color: "#dc2626",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 8px",
    flexShrink: 0,
    transition: "background 0.15s",
    lineHeight: 1.4,
  },
  error: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.4)",
    borderRadius: "6px",
    color: "#dc2626",
    fontSize: "13px",
    padding: "10px 14px",
    marginTop: "12px",
  },
  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #fde68a",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexShrink: 0,
  },
  btn: {
    padding: "10px 24px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    border: "none",
    fontFamily: "inherit",
    transition: "opacity 0.2s",
  },
  cancelBtn: { background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff" },
  saveBtn: { background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)", color: "#fff" },
};
